import React, { useState, useEffect, useRef } from 'react'

import { SecondaryBase } from './../Commons/SecondaryBase'

import { MESSAGE_TYPE, useFloatMessage } from './../../Providers/FloatMessage'
import { StartNoisemeter as BLStartNoisemeter, StopNoisemeter as BLStopNoisemeter, ResetNoisemeter as BLResetNoisemeter, GetNoisemeterStats as BLGetNoisemeterStats, GetZonesList as BLGetZonesList } from './../../../utilities/BL/Admin/Overlays/Noisemeter'


const Noisemeter = ({ getOutOfTheDynamic }) => {
    const { showMessage, MESSAGE_TYPE } = useFloatMessage();

    // --- CONSTANTES ---
    const STATUS = {
        WAITING: 'Waiting', // Coincide con el Enum del C#
        RUNNING: 'Running',
        FINISHED: 'Finished'
    };

    // --- ESTADOS ---
    const [phase, setPhase] = useState(STATUS.WAITING);
    const [isWorking, setIsWorking] = useState(false);
    const [error, setError] = useState('');

    // Configuración
    const [duration, setDuration] = useState(() => +localStorage.getItem('adm.noise.duration') || 30);
    const [sensitivity, setSensitivity] = useState(() => +localStorage.getItem('adm.noise.sensitivity') || 1.0);
    const [targetZone, setTargetZone] = useState('all'); 
    
    // Datos Dinámicos
    const [availableZones, setAvailableZones] = useState([]); // Lista de zonas traída de la API
    const [loadingZones, setLoadingZones] = useState(false);
    
    // Estadísticas en Vivo
    const [stats, setStats] = useState({
        zones: [],
        activeZone: 'all',
        winner: 'none'
    });

    const pollingRef = useRef(null);

    // --- EFECTOS: PERSISTENCIA ---
    useEffect(() => { localStorage.setItem('adm.noise.duration', duration); }, [duration]);
    useEffect(() => { localStorage.setItem('adm.noise.sensitivity', sensitivity); }, [sensitivity]);

    // --- EFECTO: CARGAR ZONAS AL INICIO ---
    useEffect(() => {
        loadZones();
        return () => stopPolling();
    }, []);

    // --- EFECTO: POLLING AUTOMÁTICO ---
    useEffect(() => {
        // Si estamos corriendo o terminados, iniciamos el polling para ver la barra subir
        if (phase === STATUS.RUNNING || phase === STATUS.FINISHED) {
            startPolling();
        } else {
            // Si estamos en waiting, hacemos UN solo fetch para ver cuántos usuarios hay conectados (útil para validar)
            fetchStatsOnce(); 
            stopPolling();
        }
    }, [phase]);

    // --- FUNCIONES AUXILIARES ---

    const loadZones = async () => {
        setLoadingZones(true);
        try {
            // Esta función del BL llama al endpoint genérico /admin/GetActiveZones
            const response = await BLGetZonesList(); 
            if (response.success && Array.isArray(response.data)) {
                setAvailableZones(response.data);
                
                // Si la zona seleccionada ya no existe (ej. cambio Varonil->Femenil), resetear a 'all'
                if (targetZone !== 'all' && !response.data.includes(targetZone)) {
                    setTargetZone('all');
                }
            }
        } catch (e) {
            console.error("Error cargando zonas:", e);
        } finally {
            setLoadingZones(false);
        }
    };

    const fetchStatsOnce = async () => {
        try {
            const response = await BLGetNoisemeterStats();
            if (response.success && response.data) {
                updateLocalStats(response.data);
            }
        } catch (error) { console.log(error); }
    };

    const startPolling = () => {
        stopPolling();
        fetchStatsOnce(); // Llamada inmediata
        pollingRef.current = setInterval(fetchStatsOnce, 1000); // Cada 1s
    };

    const stopPolling = () => {
        if (pollingRef.current) clearInterval(pollingRef.current);
    };

    const updateLocalStats = (data) => {
        // Sincronizar estado del servidor con local
        if (data.status) {
            // Mapeo simple por si el backend manda int o string
            const serverStatus = typeof data.status === 'number' 
                ? (data.status === 1 ? STATUS.RUNNING : data.status === 2 ? STATUS.FINISHED : STATUS.WAITING)
                : data.status;
            
            if (serverStatus !== phase) setPhase(serverStatus);
        }

        setStats({
            zones: data.zones || [],
            activeZone: data.activeZone || 'all',
            winner: data.winnerZone || 'none'
        });
    };

    // --- HANDLERS (BOTONES) ---

    const ReturnToDashboard = () => {
        getOutOfTheDynamic();
    };

    const StartGame = async () => {
        setError('');
        setIsWorking(true);

        const payload = {
            durationSeconds: Math.max(5, parseInt(duration)),
            countdownSeconds: 3,
            sensitivity: parseFloat(sensitivity),
            targetZone: targetZone
        };

        const response = await BLStartNoisemeter(payload);

        if (response.success) {
            setPhase(STATUS.RUNNING);
            showMessage({ type: MESSAGE_TYPE.SUCCESS, message: '¡Ladrómetro iniciado!' });
        } else {
            // Aquí mostramos el error si faltan usuarios (Bad Request del controller)
            setError(response.message);
            showMessage({ type: MESSAGE_TYPE.BAD, message: response.message });
        }
        setIsWorking(false);
    };

    const StopGame = async () => {
        setIsWorking(true);
        await BLStopNoisemeter();
        setPhase(STATUS.FINISHED);
        setIsWorking(false);
    };

    const ResetGame = async () => {
        setIsWorking(true);
        await BLResetNoisemeter();
        setPhase(STATUS.WAITING);
        setStats({ zones: [], activeZone: 'all', winner: 'none' });
        setError('');
        setIsWorking(false);
    };

    // --- RENDER ---

    return (
        <SecondaryBase actionToExecute={ReturnToDashboard}>
            <section className='AdmRunToGoal__Settings'>
                
                {/* Header */}
                <p className='AdmRunToGoal__Title'>
                    <i className="bi bi-soundwave"></i> Ladrómetro
                </p>

                <div className='AdmRunToGoal__Settings__Controls'>
                    
                    {/* Estatus */}
                    <div className='AdmRunToGoal__Text--Center'>
                        <p>
                            Estatus: 
                            {phase === STATUS.WAITING && <span> ⏳ Esperando configuración</span>}
                            {phase === STATUS.RUNNING && <span style={{color:'#198754'}}> 🔥 Midiendo Ruido...</span>}
                            {phase === STATUS.FINISHED && <span style={{color:'#dc3545'}}> 🏁 Finalizado</span>}
                        </p>
                    </div>

                    {/* PANEL DE ESTADÍSTICAS (Visible siempre para ver usuarios conectados antes de iniciar) */}
                    <div className='AdmRunToGoal__Stats AdmRunToGoal__SpaceUp'> {/* style={{background: '#f8f9fa', padding: '15px', borderRadius: '8px'}} */}
                        <p style={{fontWeight:'bold'}}>Monitoreo en vivo:</p>
                        {stats.zones.length === 0 ? (
                            <small>No hay datos disponibles aún.</small>
                        ) : (
                            <ul style={{paddingLeft: '0', listStyle: 'none'}}>
                                {stats.zones.map(z => (
                                    <li key={z.zone} style={{marginBottom: '10px', borderBottom: '1px solid #ddd', paddingBottom: '5px'}}>
                                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                                            <span style={{textTransform:'capitalize', fontWeight:'600'}}>{z.zone}</span>
                                            <span style={{fontSize:'0.9em'}}>
                                                <i className="bi bi-person-fill"></i> {z.connectedUsers} (Max: {z.peakUsers})
                                            </span>
                                        </div>
                                        
                                        {/* Barra de progreso visual */}
                                        <div style={{height: '8px', background: '#e9ecef', borderRadius: '4px', marginTop: '5px', overflow:'hidden'}}>
                                            <div style={{
                                                height: '100%', 
                                                width: `${z.progressPercent}%`, 
                                                background: z.zone === stats.winner ? '#ffc107' : '#0d6efd',
                                                transition: 'width 0.3s ease'
                                            }}></div>
                                        </div>
                                        <div style={{fontSize: '0.8em', textAlign:'right', color:'#666'}}>
                                            Puntos: {Math.round(z.score)}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                        
                        {phase === STATUS.FINISHED && stats.winner !== 'none' && (
                            <div style={{textAlign:'center', fontWeight:'bold', marginTop:'10px', color: '#198754'}}>
                                {stats.winner === 'tie' ? '¡EMPATE!' : `🏆 GANADOR: ${stats.winner.toUpperCase()}`}
                            </div>
                        )}
                    </div>

                    {/* CONFIGURACIÓN (Solo en Waiting) */}
                    <div className={phase !== STATUS.WAITING ? 'AdmRunToGoal__DisabledSection' : ''}>
                        
                        <label className='AdmRunToGoal__Settings__Controls__Labels AdmRunToGoal__SpaceUp'>
                            Modo / Zonas Activas:
                        </label>
                        <div style={{display:'flex', gap:'8px'}}>
                            <select 
                                className='AdmRunToGoal__Settings__Controls__Inputs'
                                value={targetZone}
                                onChange={e => setTargetZone(e.target.value)}
                                disabled={phase !== STATUS.WAITING}
                                style={{flex: 1}}
                            >
                                <option value="all">⚡ Guerra Simultánea (Todos)</option>
                                {availableZones.map((zName) => (
                                    <option key={zName} value={zName}>
                                        Solo {zName.charAt(0).toUpperCase() + zName.slice(1)}
                                    </option>
                                ))}
                            </select>
                            
                            {/* Botón de recarga de zonas */}
                            {phase === STATUS.WAITING && (
                                <button 
                                    type="button" 
                                    className="Btn__Adsl--RedOutlineTransparent" 
                                    onClick={loadZones}
                                    style={{width:'auto', padding:'0 12px'}}
                                    title="Actualizar lista de zonas"
                                >
                                    <i className={`bi bi-arrow-repeat ${loadingZones ? 'spin' : ''}`}></i>
                                </button>
                            )}
                        </div>

                        <label className='AdmRunToGoal__Settings__Controls__Labels'>Duración (segundos):</label>
                        <input 
                            className='AdmRunToGoal__Settings__Controls__Inputs' 
                            type='number' 
                            min="5" 
                            value={duration}
                            onChange={e => setDuration(e.target.value)}
                            disabled={phase !== STATUS.WAITING}
                        />

                        <label className='AdmRunToGoal__Settings__Controls__Labels'>Sensibilidad (Multiplicador):</label>
                        <input 
                            className='AdmRunToGoal__Settings__Controls__Inputs' 
                            type='number' 
                            step="0.1" 
                            min="0.1" 
                            max="5.0"
                            value={sensitivity}
                            onChange={e => setSensitivity(e.target.value)}
                            disabled={phase !== STATUS.WAITING}
                        />
                        <small className="AdmRunToGoal__Help text-small">Menor a 1 = más difícil. 1 = Normal. Mayor a 1 = más fácil</small>
                    </div>

                    {/* BOTONES */}
                    <div className='AdmRunToGoal__SpaceUp' style={{marginTop: '25px'}}>
                        
                        {phase === STATUS.WAITING && (
                            <button 
                                type='button' 
                                className={`Btn__Adsl--RedOutlineTransparent AdmRunToGoal__Settings__Controls__Buttons ${isWorking ? 'disabled' : ''}`}
                                onClick={StartGame}
                                disabled={isWorking}
                            >
                                {isWorking ? 'Iniciando...' : <><i className='bi bi-play-circle'></i> Iniciar Dinámica</>}
                            </button>
                        )}

                        {phase === STATUS.RUNNING && (
                            <button 
                                type='button' 
                                className='Btn__Adsl--RedOutlineTransparent AdmRunToGoal__Settings__Controls__Buttons'
                                onClick={StopGame}
                                style={{color: '#dc3545', borderColor: '#dc3545'}}
                            >
                                <i className='bi bi-stop-circle'></i> Detener
                            </button>
                        )}

                        {(phase === STATUS.RUNNING || phase === STATUS.FINISHED) && (
                            <button 
                                type='button' 
                                className='Btn__Adsl--RedOutlineTransparent AdmRunToGoal__Settings__Controls__Buttons'
                                onClick={ResetGame}
                                style={{marginTop: '10px'}}
                            >
                                <i className='bi bi-arrow-counterclockwise'></i> Reiniciar
                            </button>
                        )}
                    </div>

                    {/* MENSAJE DE ERROR */}
                    {error && (
                        <p className='AdmRunToGoal__Settings__Controls__Error' style={{color: 'red', marginTop: '10px', fontWeight: 'bold'}}>
                            {error}
                        </p>
                    )}

                </div>
            </section>
        </SecondaryBase>
    );
};

export { Noisemeter };























// const Noisemeter = ({ getOutOfTheDynamic }) => {
//     const { showMessage } = useFloatMessage()

//     // --- ESTADOS DE LA UI ---
//     const STATUS = {
//         WAITING: 'Waiting',
//         RUNNING: 'Running',
//         FINISHED: 'Finished'
//     };

//     const [phase, setPhase] = useState(STATUS.WAITING);
//     const [isWorking, setIsWorking] = useState(false); // Para deshabilitar botones durante peticiones
//     const [error, setError] = useState('');

//     // --- CONFIGURACIÓN (Inputs del Admin) ---
//     // Usamos localStorage para recordar la última configuración y agilizar la operación en el estadio
//     const [duration, setDuration] = useState(() => +localStorage.getItem('adm.noise.duration') || 30);
//     const [sensitivity, setSensitivity] = useState(() => +localStorage.getItem('adm.noise.sensitivity') || 1.0);
//     const [targetZone, setTargetZone] = useState('all'); // 'all', 'azul', 'oro', etc.

//     // --- ESTADÍSTICAS EN VIVO ---
//     const [stats, setStats] = useState({
//         zones: [],
//         activeZone: 'all',
//         winner: 'none'
//     });

//     // Refs para el polling (actualización automática)
//     const pollingRef = useRef(null);
    
//     const [availableZones, setAvailableZones] = useState([]); // Estado para el select
//     const [loadingZones, setLoadingZones] = useState(false); // Estado visual de carga

//     // --- PERSISTENCIA DE CONFIGURACIÓN ---
//     useEffect(() => { localStorage.setItem('adm.noise.duration', duration); }, [duration]);
//     useEffect(() => { localStorage.setItem('adm.noise.sensitivity', sensitivity); }, [sensitivity]);

//     // --- CICLO DE VIDA: POLLING DE ESTADÍSTICAS ---
//     useEffect(() => {
//         const fetchStats = async () => {
//             try {
//                 const response = await BLGetNoisemeterStats();
//                 if (response.success && response.data) {
//                     const data = response.data;
                    
//                     // Sincronizar fase local con el servidor
//                     if (data.status) setPhase(data.status);

//                     setStats({
//                         zones: data.zones || [],
//                         activeZone: data.activeZone || 'all',
//                         winner: data.winnerZone || 'none'
//                     });
//                 }
//             } catch (err) {
//                 console.error("Error polling stats:", err);
//             }
//         };

//         // Iniciar polling si estamos corriendo o acabamos de terminar (para ver resultados finales)
//         if (phase === STATUS.RUNNING || phase === STATUS.FINISHED) {
//             fetchStats(); // Primer fetch inmediato
//             pollingRef.current = setInterval(fetchStats, 1000); // Actualizar cada segundo
//         } else {
//             // Si volvemos a waiting, limpiamos stats visuales
//             if (pollingRef.current) clearInterval(pollingRef.current);
//         }

//         return () => {
//             if (pollingRef.current) clearInterval(pollingRef.current);
//         };
//     }, [phase]);

//     // --- HANDLERS (ACCIONES) ---

//     const handleReturn = async () => {
//         // Opcional: Resetear al salir para no dejar el juego "colgado"
//         // await BLResetNoisemeter(); 
//         getOutOfTheDynamic();
//     };

//     const handleStart = async () => {
//         setError('');
//         setIsWorking(true);

//         try {
//             const payload = {
//                 durationSeconds: Math.max(5, parseInt(duration)),
//                 countdownSeconds: 3, // Fijo o configurable si deseas
//                 sensitivity: parseFloat(sensitivity),
//                 targetZone: targetZone
//             };

//             const response = await BLStartNoisemeter(payload);

//             if (response.success) {
//                 setPhase(STATUS.RUNNING);
//                 showMessage({ type: MESSAGE_TYPE.SUCCESS, message: '¡Ladrómetro iniciado!' });
//             } else {
//                 // Aquí capturamos el error de "Faltan usuarios" (400 Bad Request)
//                 setError(response.message || 'No se pudo iniciar.');
//                 showMessage({ type: MESSAGE_TYPE.BAD, message: response.message });
//             }
//         } catch (err) {
//             setError('Error de conexión.');
//         } finally {
//             setIsWorking(false);
//         }
//     };

//     const handleStop = async () => {
//         setIsWorking(true);
//         try {
//             await BLStopNoisemeter();
//             setPhase(STATUS.FINISHED);
//             showMessage({ type: MESSAGE_TYPE.INFO, message: 'Dinámica detenida manualmente.' });
//         } catch (err) {
//             console.error(err);
//         } finally {
//             setIsWorking(false);
//         }
//     };

//     const handleReset = async () => {
//         setIsWorking(true);
//         setError('');
//         try {
//             await BLResetNoisemeter();
//             setPhase(STATUS.WAITING);
//             setStats({ zones: [], activeZone: 'all', winner: 'none' });
//             showMessage({ type: MESSAGE_TYPE.INFO, message: 'Listo para nueva partida.' });
//         } catch (err) {
//             console.error(err);
//         } finally {
//             setIsWorking(false);
//         }
//     };

//     // Función para cargar zonas (extraída para poder reusarla)
//     const loadZones = async () => {
//         setLoadingZones(true);
//         // Nota: Asegúrate de que tu BL llame al DL que modificamos
//         const response = await BLGetZonesList(); 

//         if (response.success && Array.isArray(response.data)) {
//             setAvailableZones(response.data);
            
//             // Si la zona seleccionada actualmente ya no existe en la nueva lista, resetear a 'all'
//             if (targetZone !== 'all' && !response.data.includes(targetZone)) {
//                 setTargetZone('all');
//             }
//         }

//         setLoadingZones(false);
//     };

//     useEffect(() => {
//         loadZones();
//     }, []);


//     return (
//         <SecondaryBase actionToExecute={handleReturn}>
//             <section className='AdmRunToGoal__Settings'> {/* Reusando clases de Balloons */}
                
//                 {/* TÍTULO */}
//                 <p className='AdmRunToGoal__Title'><i className="bi bi-soundwave"></i> Ladrómetro</p>

//                 <div className='AdmRunToGoal__Settings__Controls'>
                    
//                     {/* ESTADO DEL SISTEMA */}
//                     <div className='AdmRunToGoal__Text--Center'>
//                         <p className="AdmRunToGoal__Status">
//                             Estatus: 
//                             <span className={phase === STATUS.RUNNING ? 'text-success' : ''}>
//                                 {phase === STATUS.WAITING && ' ⏳ Esperando configuración'}
//                                 {phase === STATUS.RUNNING && ' 🔥 Midiendo Ruido...'}
//                                 {phase === STATUS.FINISHED && ' 🏁 Finalizado'}
//                             </span>
//                         </p>
//                     </div>

//                     {/* PANEL DE ESTADÍSTICAS EN VIVO (Solo visible en Running/Finished) */}
//                     {(phase === STATUS.RUNNING || phase === STATUS.FINISHED) && (
//                         <div className='AdmRunToGoal__Stats AdmRunToGoal__SpaceUp' style={{backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px'}}>
//                             <p style={{fontWeight: 'bold', marginBottom: '10px'}}>Resultados en Vivo:</p>
                            
//                             {stats.zones.length === 0 ? (
//                                 <p>Esperando datos...</p>
//                             ) : (
//                                 <ul style={{listStyle: 'none', padding: 0}}>
//                                     {stats.zones.map((z) => (
//                                         <li key={z.zone} style={{marginBottom: '8px', borderBottom: '1px solid #eee', paddingBottom: '5px'}}>
//                                             <div style={{display: 'flex', justifyContent: 'space-between'}}>
//                                                 <strong>{z.zone.toUpperCase()}</strong>
//                                                 <span>{Math.round(z.progressPercent)}% (Pts: {Math.round(z.score)})</span>
//                                             </div>
//                                             <div style={{fontSize: '0.85em', color: '#666'}}>
//                                                 <i className="bi bi-people-fill"></i> {z.connectedUsers} activos (Pico: {z.peakUsers})
//                                             </div>
//                                             {/* Barra visual pequeña para feedback inmediato */}
//                                             <div style={{height: '6px', background: '#e0e0e0', borderRadius: '3px', marginTop: '4px'}}>
//                                                 <div style={{
//                                                     height: '100%', 
//                                                     width: `${z.progressPercent}%`, 
//                                                     background: z.zone === stats.winner ? '#ffc107' : '#0d6efd',
//                                                     borderRadius: '3px',
//                                                     transition: 'width 0.5s'
//                                                 }}></div>
//                                             </div>
//                                         </li>
//                                     ))}
//                                 </ul>
//                             )}

//                             {phase === STATUS.FINISHED && (
//                                 <div style={{textAlign: 'center', marginTop: '15px', fontWeight: 'bold', color: '#198754'}}>
//                                     {stats.winner === 'tie' ? '¡EMPATE!' : 
//                                      stats.winner !== 'none' ? `¡GANADOR: ${stats.winner.toUpperCase()}!` : ''}
//                                 </div>
//                             )}
//                         </div>
//                     )}

//                     {/* CONTROLES DE CONFIGURACIÓN (Solo editables en Waiting) */}
//                     <div className={phase !== STATUS.WAITING ? 'AdmRunToGoal__DisabledSection' : ''}>
                        
//                         {/* 1. Selección de Zona (Modo de Juego) */}
//                         {/* <label className='AdmRunToGoal__Settings__Controls__Labels AdmRunToGoal__SpaceUp'>Modo de Juego:</label>
//                         <select className='AdmRunToGoal__Settings__Controls__Inputs' value={targetZone} onChange={(e) => setTargetZone(e.target.value)} disabled={phase !== STATUS.WAITING}>
//                             <option value="all">⚡ Guerra Simultánea (Todos)</option>
                            
//                             {/* Mapeo dinámico de zonas /}
//                             {availableZones.map(zoneName => (
//                                 <option key={zoneName} value={zoneName}>
//                                     Solo {zoneName.charAt(0).toUpperCase() + zoneName.slice(1)}
//                                 </option>
//                             ))}
//                         </select> */}
//                         <label className='AdmRunToGoal__Settings__Controls__Labels AdmRunToGoal__SpaceUp'>Modo de Juego / Zonas Activas:</label>
                        
//                         <div style={{ display: 'flex', gap: '10px' }}>
//                             <select className='AdmRunToGoal__Settings__Controls__Inputs' value={targetZone} onChange={(e) => setTargetZone(e.target.value)} disabled={phase !== STATUS.WAITING} style={{ flex: 1 }}>
//                                 <option value="all">⚡ Guerra Simultánea (Todas las zonas)</option>
                                
//                                 {availableZones.map(zoneName => (
//                                     <option key={zoneName} value={zoneName}>
//                                         Solo {zoneName}
//                                         {/* Solo {zoneName.charAt(0).toUpperCase() + zoneName.slice(1)} */}
//                                     </option>
//                                 ))}
//                             </select>

//                             {/* BOTÓN DE RECARGAR ZONAS */}
//                             {phase === STATUS.WAITING && (
//                                 <button type="button" className="Btn__Adsl--RedOutlineTransparent" onClick={loadZones} title="Actualizar lista de zonas (si cambiaste de Varonil/Femenil)" style={{ width: 'auto', padding: '0 15px', marginTop: '0' }}>
//                                     <i className={`bi bi-arrow-repeat ${loadingZones ? 'spin-animation' : ''}`}></i>
//                                 </button>
//                             )}
//                         </div>
//                         {/* Pequeña ayuda visual */}
//                         <small className="text-muted" style={{ fontSize: '0.8em', display: 'block', marginBottom: '10px' }}>
//                             Zonas detectadas: {availableZones.length}. Si faltan, presiona el botón de actualizar.
//                         </small>

//                         {/* 2. Duración */}
//                         <label className='AdmRunToGoal__Settings__Controls__Labels'>Duración (segundos):</label>
//                         <input 
//                             className='AdmRunToGoal__Settings__Controls__Inputs' 
//                             type='number' 
//                             min={5} 
//                             max={300}
//                             value={duration} 
//                             onChange={(e) => setDuration(e.target.value)}
//                             disabled={phase !== STATUS.WAITING}
//                         />

//                         {/* 3. Sensibilidad */}
//                         <label className='AdmRunToGoal__Settings__Controls__Labels'>Sensibilidad del Micrófono (Multiplicador):</label>
//                         <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
//                             <input 
//                                 className='AdmRunToGoal__Settings__Controls__Inputs' 
//                                 type='number' 
//                                 step="0.1"
//                                 min={0.1} 
//                                 max={5.0}
//                                 value={sensitivity} 
//                                 onChange={(e) => setSensitivity(e.target.value)}
//                                 disabled={phase !== STATUS.WAITING}
//                             />
//                             <small className="text-muted">
//                                 (1.0 = Normal. &gt;1 = Más fácil llenar barra)
//                             </small>
//                         </div>
//                     </div>

//                     {/* BOTONES DE ACCIÓN */}
//                     <div className='AdmRunToGoal__SpaceUp' style={{marginTop: '30px'}}>
                        
//                         {/* Botón START (Solo en Waiting) */}
//                         {phase === STATUS.WAITING && (
//                             <button 
//                                 type='button' 
//                                 className={`Btn__Adsl--RedOutlineTransparent AdmRunToGoal__Settings__Controls__Buttons ${isWorking ? 'disabled' : ''}`}
//                                 onClick={handleStart}
//                                 disabled={isWorking}
//                             >
//                                 {isWorking ? 'Iniciando...' : <><i className='bi bi-play-circle'></i> Iniciar Dinámica</>}
//                             </button>
//                         )}

//                         {/* Botón STOP (Solo en Running) */}
//                         {phase === STATUS.RUNNING && (
//                             <button 
//                                 type='button' 
//                                 className='Btn__Adsl--RedOutlineTransparent AdmRunToGoal__Settings__Controls__Buttons'
//                                 onClick={handleStop}
//                                 style={{borderColor: '#dc3545', color: '#dc3545'}} // Rojo alerta
//                             >
//                                 <i className='bi bi-stop-circle'></i> Detener Dinámica
//                             </button>
//                         )}

//                         {/* Botón RESET (En Running o Finished) */}
//                         {(phase === STATUS.RUNNING || phase === STATUS.FINISHED) && (
//                             <button 
//                                 type='button' 
//                                 className='Btn__Adsl--RedOutlineTransparent AdmRunToGoal__Settings__Controls__Buttons'
//                                 onClick={handleReset}
//                                 style={{marginTop: '10px'}}
//                             >
//                                 <i className='bi bi-arrow-counterclockwise'></i> Reiniciar / Nueva Partida
//                             </button>
//                         )}
//                     </div>

//                     {/* MENSAJES DE ERROR */}
//                     {error && (
//                         <p className='AdmRunToGoal__Settings__Controls__Error' style={{marginTop: '15px', color: 'red', fontWeight: 'bold'}}>
//                             <i className="bi bi-exclamation-triangle"></i> {error}
//                         </p>
//                     )}

//                 </div>
//             </section>
//         </SecondaryBase>
//     );
// };

// export { Noisemeter };



















// const Noisemeter = ({ getOutOfTheDynamic }) => {
//     const { showMessage } = useFloatMessage()


//     const ReturnToDashboard = async () => {
//         try {
//             getOutOfTheDynamic()
//         } catch {
//             getOutOfTheDynamic()
//         }
//     }

//     const StartGame = async () => {
//         try {
//             const response = await BLStartNoisemeter()

//             if (response.success) {
//                 showMessage({ type: MESSAGE_TYPE.BAD, message: 'Ladrómetro iniciado con éxito' })
//             } else {
//                 showMessage({ type: MESSAGE_TYPE.BAD, message: response.message })
//             }
//         } catch (error) {
//             showMessage({ type: MESSAGE_TYPE.BAD, message: 'No ha sido posible iniciar el Ladrómetro. Inténtalo nuevamente o inténtalo más tarde' })
//         }
//     }


//     return (
//         <SecondaryBase actionToExecute={ReturnToDashboard}>
//             <p>Ladrómetro</p>

//             <p>Establece los valores que desees para la dinámica:</p>

//             <label>Valor X</label>
//             <input type='text' placeholder='Valor' />

//             <button type='button' className='Btn__Adsl--RedOutlineTransparent' onClick={() => StartGame()}><i className='bi bi-play-circle'></i> Iniciar dinámica</button>
            
//             <button type='button' className='Btn__Adsl--RedOutlineTransparent'><i className='bi bi-bar-chart'></i> Mostrar resultados</button>
//         </SecondaryBase>
//     )
// }

// export { Noisemeter };